import apiClient from './api-client';

const STATE_FIPS_BY_CODE = {
  AL: '01', AK: '02', AZ: '04', AR: '05', CA: '06', CO: '08', CT: '09',
  DE: '10', DC: '11', FL: '12', GA: '13', HI: '15', ID: '16', IL: '17',
  IN: '18', IA: '19', KS: '20', KY: '21', LA: '22', ME: '23', MD: '24',
  MA: '25', MI: '26', MN: '27', MS: '28', MO: '29', MT: '30', NE: '31',
  NV: '32', NH: '33', NJ: '34', NM: '35', NY: '36', NC: '37', ND: '38',
  OH: '39', OK: '40', OR: '41', PA: '42', RI: '44', SC: '45', SD: '46',
  TN: '47', TX: '48', UT: '49', VT: '50', VA: '51', WA: '53', WV: '54',
  WI: '55', WY: '56'
};

const VALID_STATE_FIPS = new Set(Object.values(STATE_FIPS_BY_CODE));
const CACHE_TTL_MS = 3 * 60 * 60 * 1000;
const utilityAssetCache = new Map();
let utilityCatalogCache = null;

const getCachedPromise = (cache, key, loader) => {
  const now = Date.now();
  const cached = cache.get(key);
  if (cached && cached.expiresAt > now) return cached.promise;

  cache.delete(key);
  const promise = Promise.resolve()
    .then(loader)
    .catch((error) => {
      cache.delete(key);
      throw error;
    });
  cache.set(key, { promise, expiresAt: now + CACHE_TTL_MS });
  return promise;
};

export const normalizeStateFips = (stateId) => {
  const normalized = String(stateId ?? '').trim().toUpperCase();
  const fips = STATE_FIPS_BY_CODE[normalized]
    || (/^\d{1,2}$/.test(normalized) ? normalized.padStart(2, '0') : null);

  if (!fips || !VALID_STATE_FIPS.has(fips)) {
    throw new Error('Utility view requires a valid U.S. state or District of Columbia.');
  }
  return fips;
};

const calculatePolygonArea = (coordinates) => {
  if (!coordinates || coordinates.length === 0) return 0;
  const ring = coordinates[0];
  let area = 0;
  for (let index = 0; index < ring.length - 1; index += 1) {
    const [x1, y1] = ring[index];
    const [x2, y2] = ring[index + 1];
    area += (x2 - x1) * (y2 + y1);
  }
  return Math.abs(area / 2);
};

const getFeatureArea = (feature) => {
  if (feature.geometry?.type === 'Polygon') {
    return calculatePolygonArea(feature.geometry.coordinates);
  }
  if (feature.geometry?.type === 'MultiPolygon') {
    return feature.geometry.coordinates.reduce(
      (sum, coordinates) => sum + calculatePolygonArea(coordinates),
      0
    );
  }
  return 0;
};

const normalizeMetadata = (metadata, expectedFips) => {
  if (!metadata || typeof metadata !== 'object') {
    throw new Error('Utility GeoJSON is missing state metadata.');
  }

  const stateFips = normalizeStateFips(metadata.state_fips);
  if (expectedFips && stateFips !== expectedFips) {
    throw new Error(`Utility GeoJSON state mismatch: expected ${expectedFips}, received ${stateFips}.`);
  }
  if (!metadata.state_code || !metadata.state_name) {
    throw new Error(`Utility GeoJSON metadata is incomplete for state ${stateFips}.`);
  }

  return {
    state_fips: stateFips,
    state_code: String(metadata.state_code),
    state_name: String(metadata.state_name),
    utility_count: Number(metadata.utility_count)
  };
};

export const normalizeUtilityGeoJson = (geoJson, expectedStateId) => {
  if (!geoJson || geoJson.type !== 'FeatureCollection' || !Array.isArray(geoJson.features)) {
    throw new Error('Utility GeoJSON is invalid.');
  }

  const expectedFips = expectedStateId === undefined
    ? undefined
    : normalizeStateFips(expectedStateId);
  const metadata = normalizeMetadata(geoJson.metadata, expectedFips);
  const ids = new Set();
  const features = geoJson.features.map((feature) => {
    const properties = feature?.properties;
    const companyId = String(properties?.company_id ?? '').trim();
    const customers = Number(properties?.utility_customers);
    const geometryType = feature?.geometry?.type;

    if (
      !companyId
      || ids.has(companyId)
      || !properties?.utility_name
      || !Number.isFinite(customers)
      || customers <= 0
      || !['Polygon', 'MultiPolygon'].includes(geometryType)
      || getFeatureArea(feature) <= 0
    ) {
      throw new Error(`Utility GeoJSON contains an invalid territory for state ${metadata.state_fips}.`);
    }
    ids.add(companyId);

    return {
      ...feature,
      id: companyId,
      properties: {
        ...properties,
        company_id: companyId,
        utility_customers: customers
      }
    };
  });

  if (features.length === 0 || features.length > 5) {
    throw new Error(`Utility GeoJSON must contain one to five territories for state ${metadata.state_fips}.`);
  }
  if (metadata.utility_count !== features.length) {
    throw new Error(`Utility GeoJSON territory count is invalid for state ${metadata.state_fips}.`);
  }

  // Draw larger territories first so overlapping smaller territories remain selectable.
  features.sort((left, right) => getFeatureArea(right) - getFeatureArea(left));
  return { ...geoJson, metadata, features };
};

export const deriveUtilityCompanies = (geoJson) => geoJson.features.map((feature) => ({
  id: feature.properties.company_id,
  name: String(feature.properties.utility_name),
  shortName: String(feature.properties.utility_short_name || feature.properties.utility_name),
  sourceName: String(
    feature.properties.source_utility_name
    || feature.properties.utility_short_name
    || feature.properties.utility_name
  ),
  customers: Number(feature.properties.utility_customers)
}));

const loadUtilityAsset = (stateId) => {
  const stateFips = normalizeStateFips(stateId);
  return getCachedPromise(utilityAssetCache, stateFips, () => (
    fetch(`/state_files/utility_geojson/${stateFips}.geojson`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Utility data for state ${stateFips} failed to load (${response.status}).`);
        }
        return response.json();
      })
      .then((geoJson) => normalizeUtilityGeoJson(geoJson, stateFips))
  ));
};

const normalizeUtilityName = (value) => String(value ?? '')
  .trim()
  .replace(/\s+/g, ' ')
  .toLocaleLowerCase('en-US');

const loadUtilityCatalog = () => {
  const now = Date.now();
  if (!utilityCatalogCache || utilityCatalogCache.expiresAt <= now) {
    const promise = apiClient.listUtilities()
      .then((utilities) => {
        if (!Array.isArray(utilities)) {
          throw new Error('Utility API returned an invalid utility list.');
        }
        return utilities;
      })
      .catch((error) => {
        utilityCatalogCache = null;
        throw error;
      });
    utilityCatalogCache = { promise, expiresAt: now + CACHE_TTL_MS };
  }
  return utilityCatalogCache.promise;
};

const resolveBackendUtilities = (companies, utilities) => {
  const utilitiesByName = new Map(utilities.map((utility) => [
    normalizeUtilityName(utility.name),
    utility
  ]));
  const unresolved = [];

  const resolved = companies.map((company) => {
    const utility = utilitiesByName.get(normalizeUtilityName(company.sourceName));
    if (!utility) {
      unresolved.push(company);
      return null;
    }
    return {
      ...company,
      backendId: Number(utility.id)
    };
  }).filter(Boolean);

  return { resolved, unresolved };
};

export const clearUtilityDataCache = () => {
  utilityAssetCache.clear();
  utilityCatalogCache = null;
};

export const loadUtilityData = async ({ stateId, startDate, endDate, timezone }) => {
  const stateFips = normalizeStateFips(stateId);
  const [geoJson, utilityCatalog] = await Promise.all([
    loadUtilityAsset(stateFips),
    loadUtilityCatalog()
  ]);
  const { resolved: resolvedCompanies, unresolved } = resolveBackendUtilities(
    deriveUtilityCompanies(geoJson),
    utilityCatalog
  );
  if (resolvedCompanies.length === 0) {
    throw new Error(`Utility API has no mapped utilities for state ${stateFips}.`);
  }

  const predictionResults = await Promise.allSettled(resolvedCompanies.map((company) => (
    apiClient.getUtilityPredictions(company.backendId, startDate, endDate, timezone)
  )));

  const companies = [];
  const predictions = {};

  unresolved.forEach((company) => {
    console.warn(`Utility API is missing ${company.sourceName}; omitting its territory.`);
  });

  predictionResults.forEach((result, index) => {
    const company = resolvedCompanies[index];
    if (result.status === 'rejected') {
      console.warn(`Utility ${company.sourceName} predictions failed to load.`, result.reason);
      return;
    }

    const response = result.value;
    const totalCustomers = Number(response.total_customers) || company.customers;
    const utilityPredictions = (response.predictions || []).map((entry) => ({
      date: String(entry.date || '').slice(0, 10),
      customers: {
        min: Number(entry.min) || 0,
        mean: Number(entry.mean) || 0,
        max: Number(entry.max) || 0,
        weather_severity: Number(entry.weather_severity) || 0
      }
    }));

    companies.push({ ...company, customers: totalCustomers });
    predictions[company.id] = utilityPredictions;
  });

  if (companies.length === 0 && resolvedCompanies.length > 0) {
    throw new Error(`Utility predictions for state ${stateFips} failed to load.`);
  }

  const availableIds = new Set(companies.map((company) => company.id));
  const availableFeatures = geoJson.features.filter((feature) => (
    availableIds.has(String(feature.properties.company_id))
  ));
  const availableGeoJson = {
    ...geoJson,
    metadata: { ...geoJson.metadata, utility_count: availableFeatures.length },
    features: availableFeatures
  };

  return {
    state: {
      fips: geoJson.metadata.state_fips,
      code: geoJson.metadata.state_code,
      name: geoJson.metadata.state_name
    },
    companies,
    predictions,
    geoJson: availableGeoJson
  };
};
